/**
 * GENERATED_ASSET — closed recipe/token validation ported from the batch001 static kit
 * (`static-kit/source/validation.ts`). The lantern, ribbon, reed and paper-boat generator families are adopted;
 * every other generator id is rejected as unsupported.
 */
import type { StaticRecipe, Tokens } from './contracts';

export const ROLES = [
  'outline', 'outlineSoft', 'paperBase', 'paperLight', 'paperShadow', 'warmCore',
  'woodBase', 'woodLight', 'woodShadow', 'clothBlue', 'clothBlueLight', 'clothBlueShadow',
  'reedBase', 'reedLight', 'reedShadow', 'stoneBase', 'stoneLight', 'stoneShadow', 'moss', 'salt', 'waterFar', 'waterLight',
] as const;

export function record(v: unknown, label: string): Record<string, unknown> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) throw new Error(`${label}: expected object`);
  return v as Record<string, unknown>;
}

export function keys(v: Record<string, unknown>, allowed: readonly string[], label: string): void {
  for (const k of Object.keys(v)) if (!allowed.includes(k)) throw new Error(`${label}: unknown key ${k}`);
  for (const k of allowed) if (!(k in v)) throw new Error(`${label}: missing ${k}`);
}

export function one(v: unknown, values: readonly unknown[], label: string): void {
  if (!values.includes(v)) throw new Error(`${label}: unsupported value ${String(v)}`);
}

export function range(v: unknown, min: number, max: number, label: string, integer = false): number {
  if (typeof v !== 'number' || !Number.isFinite(v) || v < min || v > max || (integer && !Number.isInteger(v))) {
    throw new Error(`${label}: expected ${integer ? 'integer ' : ''}${min}..${max}`);
  }
  return v;
}

export function validateRecipe(raw: unknown): StaticRecipe {
  const r = record(raw, 'recipe');
  keys(r, ['schema', 'id', 'assetId', 'generatorId', 'seed', 'paletteId', 'detail', 'params'], 'recipe');
  one(r.schema, ['hush.asset-recipe.v1'], 'schema');
  for (const k of ['id', 'assetId']) {
    if (typeof r[k] !== 'string' || !/^[a-z0-9_.-]{1,120}$/.test(r[k] as string)) throw new Error(`invalid ${k}`);
  }
  range(r.seed, 0, 0xffffffff, 'seed', true);
  one(r.paletteId, ['coastal_salt', 'nook_warm', 'icon_ink'], 'paletteId');
  one(r.detail, ['readable', 'standard', 'close'], 'detail');
  const p = record(r.params, 'params');
  switch (r.generatorId) {
  case 'hush.lantern.v1':
    one(r.assetId, ['prop.hush.three_window_lantern'], 'assetId');
    keys(p, ['condition', 'support', 'paperWear', 'warmth', 'repairNotch'], 'lantern');
    one(p.condition, ['salt_worn', 'dry', 'interior_warm'], 'condition');
    one(p.support, ['hanging', 'standing'], 'support');
    one(p.repairNotch, ['lower-left', 'lower-right', 'none'], 'repairNotch');
    range(p.paperWear, 0, 1, 'paperWear');
    range(p.warmth, 0, 1, 'warmth');
    break;
  case 'hush.ribbon.v1':
    one(r.assetId, ['prop.hush.crooked_ribbon'], 'assetId');
    keys(p, ['pose', 'tailLength', 'shortTailRatio', 'width', 'fray', 'seam'], 'ribbon');
    one(p.pose, ['rest', 'left_breeze', 'right_breeze'], 'pose');
    one(p.seam, ['none', 'short-repair'], 'seam');
    range(p.tailLength, 36, 74, 'tailLength');
    range(p.shortTailRatio, 0.55, 0.75, 'shortTailRatio');
    range(p.width, 8, 16, 'width');
    range(p.fray, 0, 2, 'fray', true);
    break;
  case 'hush.reeds.v1':
    one(r.assetId, ['prop.hush.reed_curtain'], 'assetId');
    keys(p, ['opening', 'width', 'height', 'stalksPerSide', 'gapWidth', 'tieHeightRatio', 'tipSpread'], 'reeds');
    one(p.opening, ['concealed', 'gathered_open'], 'opening');
    range(p.width, 110, 220, 'width');
    range(p.height, 100, 190, 'height');
    range(p.stalksPerSide, 5, 12, 'stalksPerSide', true);
    range(p.gapWidth, 18, 70, 'gapWidth');
    range(p.tieHeightRatio, 0.35, 0.65, 'tieHeightRatio');
    range(p.tipSpread, 8, 28, 'tipSpread');
    if ((p.width as number) - (p.gapWidth as number) < 60) throw new Error('reeds: insufficient side width');
    break;
  case 'hush.paper_boat.v1':
    one(r.assetId, ['prop.hush.paper_boat'], 'assetId');
    keys(p, ['fold', 'condition', 'clueTab', 'leanDeg', 'paperWear'], 'boat');
    one(p.fold, ['classic'], 'fold');
    one(p.condition, ['clean', 'salt_softened'], 'condition');
    one(p.clueTab, ['none', 'two_over_one'], 'clueTab');
    range(p.leanDeg, -6, 6, 'leanDeg');
    range(p.paperWear, 0, 0.6, 'paperWear');
    break;
  default:
    throw new Error(`unsupported generator ${String(r.generatorId)}`);
  }
  return raw as StaticRecipe;
}

const MASTER_CENTERS = JSON.stringify([{ x: -12, y: -56 }, { x: 12, y: -56 }, { x: 0, y: -32 }]);

export function validateTokens(raw: unknown): Tokens {
  const t = record(raw, 'tokens');
  const palettes = record(t.palettes, 'palettes');
  if (typeof t.revision !== 'string' || !t.revision) throw new Error('tokens revision required');
  keys(palettes, ['coastal_salt', 'nook_warm', 'icon_ink'], 'palettes');
  for (const [id, v] of Object.entries(palettes)) {
    const p = record(v, id);
    keys(p, ROLES, id);
    for (const [role, c] of Object.entries(p)) {
      if (typeof c !== 'string' || !/^#[0-9a-f]{6}$/i.test(c)) throw new Error(`invalid material ${id}.${role}`);
    }
  }
  const s = record(t.style, 'style');
  keys(s, ['outerStroke', 'seamStroke', 'grainStroke', 'grainOpacity', 'contactShadowOpacity', 'coordinatePrecision', 'samplingPadding'], 'style');
  for (const k of ['outerStroke', 'seamStroke', 'grainStroke']) range(s[k], 0.1, 4, k);
  for (const k of ['grainOpacity', 'contactShadowOpacity']) range(s[k], 0, 0.5, k);
  one(s.coordinatePrecision, [3], 'coordinatePrecision');
  range(s.samplingPadding, 1, 8, 'samplingPadding');
  const m = record(t.masterMotif, 'motif');
  if (JSON.stringify(m.centers) !== MASTER_CENTERS || m.radius !== 7.5 || m.protectedRadius !== 10 || m.randomization !== 'forbidden') {
    throw new Error('master two-over-one motif is immutable');
  }
  const tiers = record(t.detailTiers, 'tiers');
  keys(tiers, ['readable', 'standard', 'close'], 'tiers');
  for (const v of Object.values(tiers)) {
    const d = record(v, 'tier');
    range(d.grainMarks, 0, 20, 'grainMarks', true);
    if (typeof d.minorRepairs !== 'boolean') throw new Error('minorRepairs boolean required');
  }
  return raw as Tokens;
}
